import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';

import { dashboardsTitles, notifications, rightsTitles } from '../dictionary';
import { authUserState } from '../stores/common';

const linkedDashboardRules = [
	{
		// Для предприятий без ERP чек-листы выбираются одним пунктом верхнего меню,
		// а доступ в левом меню и виджет на рабочем столе подключаются автоматически.
		Id : 99,
		SourceType : "Menu",
		TargetTypes : ["Sidebar", "Widget"],
		OnlyWithoutERP : true
	}
];

/** ----- МОДЕЛЬ УВЕДОМЛЕНИЙ ДОСТУПА ----- */
export class NotificationsAccess {
	constructor(checked, notificationItems) {
		this.Checked = !_.isNil(checked) ? checked : [];
		this.Items = !_.isNil(notificationItems) ? notificationItems : [];
	}

	/** Справочник уведомлений без заказов, сгруппированный и подготовленный для отображения */
	get List() {
		return _.map(_.groupBy(_.reject(this.Items, { Group : "Orders" }), "Group"), (list, group) => {
			return {
				Id : group,
				Title : notifications[group] || group,
				Items : _.map(list, (item) => {
					return {
						Id : item.Id,
						Name : notifications[item.Name] || item.Name
					};
				})
			};
		});
	}

	/** Сбор данных с формы выбора уведомлений */
	collect = (id, value) => {
		if (id === "Notifications") this.Checked = value;
	}

	/** Автоматически устанавливает итоговый список выбранных уведомлений */
	setChecked = (notificationIds) => {
		this.Checked = _.uniq(notificationIds || []);
	}

	/**
	 * Сбор выбранных уведомлений
	 * @returns {[number]} список ID выбранных уведомлений
	 */
	getData = () => { return _.clone(this.Checked); }
}
decorate(NotificationsAccess, {
	Checked: observable,
	Items: observable,
	List: computed
});

/** ----- МОДЕЛЬ РАБОЧИХ СТОЛОВ ДОСТУПА ----- */
export class DashboardsAccess {
	constructor(checked, dashboards) {
		this.Checked = {
			Menu : checked && !_.isNil(checked.Menu) ? checked.Menu : [],
			Sidebar : checked && !_.isNil(checked.Sidebar) ? checked.Sidebar : [],
			Widget : checked && !_.isNil(checked.Widget) ? checked.Widget : []
		};
		this.Items = !_.isNil(dashboards) ? dashboards : [];
	}

	/** Справочник рабочих столов, сгруппированный и подготовленный для отображения */
	get List() {
		return {
			Menu : this.getListByType("Menu"),
			Sidebar : this.getListByType("Sidebar"),
			Widget : this.getListByType("Widget")
		};
	}

	/**
	 * Подготовка групп рабочих столов указанного типа для отображения
	 * @param {string} type тип рабочего стола
	 * @returns {[{ Id: string, Title: string, IsWide: boolean, Items: [object], SubGroups: [object] }]} список групп для отображения
	 */
	getListByType = (type) => {
		var items = _.filter(this.Items, { Type : type });

		if (type === "Widget") {
			return [{
				Id : "Widget",
				Title : "Виджет",
				Items : _.map(items, (item) => {
					return {
						Id : item.Id,
						Name : dashboardsTitles[item.Group] || item.Group
					};
				})
			}];
		}

		return _.map(_.groupBy(items, "Group"), (list, group) => {
			var isWide = _.uniq(_.compact(_.map(_.filter(list, (item) => item.Name !== ""),
				(item) => item.SubGroup))).length > 2;

			return {
				Id : group,
				Title : dashboardsTitles[group] || group,
				IsWide : isWide,
				Items : _.map(list, (item) => {
					var title = "";

					if (list.length === 1 && type !== "Menu") title = dashboardsTitles[group] || group;
					else title = (dashboardsTitles[item.SubGroup] || item.SubGroup)
						+ (item.Name !== "" ? " – " + (dashboardsTitles[item.Name] || item.Name) : "");

					return {
						Id : item.Id,
						Name : title
					};
				}),
				SubGroups : isWide ? _.map(_.groupBy(_.filter(list, (item) => !_.isEmpty(item.SubGroup)), "SubGroup"),
					(subGroupItems, subGroup) => {
						return {
							Id : subGroup,
							Title : dashboardsTitles[subGroup] || subGroup,
							Items : _.map(subGroupItems, (item) => {
							return {
								Id : item.Id,
								Name : item.Name !== "" ? dashboardsTitles[item.Name] || item.Name
										: dashboardsTitles[subGroup] || subGroup
								};
							})
						};
					}) : []
			};
		});
	}

	/** Сбор данных с формы выбора рабочих столов */
	collect = (id, value, type) => {
		if (id === "Dashboards") {
			this.Checked[type] = value;
			this.syncLinkedDashboardRules(type);
		}
	}

	/** Автоматически устанавливает итоговые списки выбранных рабочих столов по типам */
	setChecked = (dashboards) => {
		this.Checked = {
			Menu : dashboards && !_.isNil(dashboards.Menu) ? _.uniq(dashboards.Menu) : [],
			Sidebar : dashboards && !_.isNil(dashboards.Sidebar) ? _.uniq(dashboards.Sidebar) : [],
			Widget : dashboards && !_.isNil(dashboards.Widget) ? _.uniq(dashboards.Widget) : []
		};
		_.each(linkedDashboardRules, (rule) => { this.syncLinkedDashboardRules(rule.SourceType); });
	}

	/** Синхронизация связанных рабочих столов по итоговому набору исходного типа */
	syncLinkedDashboardRules = (type) => {
		_.each(linkedDashboardRules, (rule) => {
			if (rule.SourceType !== type) return;
			if (rule.OnlyWithoutERP && authUserState.HasERP) return;
			const checked = _.includes(this.Checked[type], rule.Id);

			_.each(rule.TargetTypes, (targetType) => {
				if (checked) this.Checked[targetType] = _.union(this.Checked[targetType], [rule.Id]);
				else _.remove(this.Checked[targetType], v => v === rule.Id);
			});
		});
	}

	/**
	 * Сбор выбранных рабочих столов по типам.
	 * Преобразование в API-формат выполняется только в финальной сборке сохраняемой сущности.
	 * @returns {{ Menu?: [number], Sidebar?: [number], Widget?: [number] }} выбранные рабочие столы по типам
	 */
	getData = () => {
		var data = {};

		_.each(this.Checked, (list, type) => {
			if (!_.isEmpty(list) && !_.isNull(list)) data[type] = _.clone(list);
		});

		return data;
	}
}
decorate(DashboardsAccess, {
	Checked: observable,
	Items: observable,
	List: computed
});

/** ----- МОДЕЛЬ ПРАВ ДОСТУПА ----- */
export class RightsAccess {
	constructor(checked, rights) {
		this.Checked = !_.isNil(checked) ? checked : [];
		this.Rights = !_.isNil(rights) ? rights : [];
	}

	/** Справочник прав, сгруппированный и подготовленный для отображения */
	get List() {
		return _.map(_.groupBy(this.Rights, "Group"), (list, group) => {
			var groupTitle = _.find(rightsTitles, { Id : group });

			return {
				Id : group,
				Title : groupTitle ? groupTitle.Name : group,
				Disabled : _.every(list, (right) => right.Disabled),
				Items : _.map(list, (right) => {
					var rightTitle = _.find(rightsTitles, { Id : right.Name });

					return {
						Id : right.Name,
						Name : rightTitle ? rightTitle.Name : right.Name,
						Disabled : right.Disabled
					};
				})
			};
		});
	}

	/** Сбор данных с формы выбора прав */
	collect = (id, value) => {
		if (id === "Rights") this.Checked = value;
	}

	/** Автоматически устанавливает итоговый список выбранных прав */
	setChecked = (rights) => {
		this.Checked = _.uniq(rights || []);
	}

	/**
	 * Сбор выбранных прав
	 * @returns {[string]} список названий выбранных прав
	 */
	getData = () => { return _.clone(this.Checked); }
}
decorate(RightsAccess, {
	Checked: observable,
	Rights: observable,
	List: computed
});
